import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Planning, PlanningSection, PlanningStep } from "types/planning";
import { PartnerInfos } from "types";
import { MODEL_WEEK_COUNTS } from "../../../utils/planning_constants";
import {
  resolveSectionFromPlanning,
  ResolvedPlanningSection,
} from "../../../utils/planning_structure";
import { useProjectStore } from "@store/projectStore";
import Icon1 from "@assets/icons/Icon";
import dragHandGif from "@assets/gifs/icons8-glisser-droite.gif";

// ─── Week column computation ─────────────────────────────────────────────────

const FRENCH_MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

interface WeekColumn {
  index: number; // 0-based column index
  isoWeek: number; // ISO week number (1-53)
  month: number; // 1-12
  monthLabel: string;
  year: number;
  isBlocked: boolean; // August OR ISO week 52 OR ISO week 1
  monday: Date;
}

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return (
    1 +
    Math.round(
      ((d.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7,
    )
  );
}

const MODELS_WITHOUT_CONGES = new Set(["12_mois_ordre"]);

function computeWeekColumns(startDate: Date, model: string): WeekColumn[] {
  const n = MODEL_WEEK_COUNTS[model] ?? 48;
  const hasConges = !MODELS_WITHOUT_CONGES.has(model);
  return Array.from({ length: n }, (_, i) => {
    const monday = new Date(startDate);
    monday.setDate(monday.getDate() + i * 7);
    const isoWeek = getISOWeek(monday);
    const calMonth = monday.getMonth() + 1; // calendar month for blocked-week logic
    // Adjust displayed month/year to match ISO week:
    // ISO week 1 in late December → display as January of next year
    // ISO week 52/53 in early January → display as December of previous year
    let month = calMonth;
    let year = monday.getFullYear();
    if (isoWeek === 1 && calMonth === 12) {
      month = 1;
      year += 1;
    } else if (isoWeek >= 52 && calMonth === 1) {
      month = 12;
      year -= 1;
    }
    return {
      index: i,
      isoWeek,
      month,
      monthLabel: FRENCH_MONTHS[month - 1],
      year,
      isBlocked: hasConges && (
        // 5 weeks in August: ISO weeks 31–35
        (isoWeek >= 31 && isoWeek <= 35) ||
        // 2 winter weeks: last 2 Mondays of December (always Dec 20-31), never spills into January
        (calMonth === 12 && monday.getDate() >= 20)
      ),
      monday,
    };
  });
}

// Group adjacent weeks by month for merged header cells
interface MonthGroup {
  label: string;
  year: number;
  colSpan: number;
}

function buildMonthGroups(cols: WeekColumn[]): MonthGroup[] {
  const groups: MonthGroup[] = [];
  for (const col of cols) {
    const last = groups[groups.length - 1];
    if (last && last.label === col.monthLabel && last.year === col.year) {
      last.colSpan++;
    } else {
      groups.push({ label: col.monthLabel, year: col.year, colSpan: 1 });
    }
  }
  return groups;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  planning: Planning;
  partners: PartnerInfos[];
  onUpdate: (updated: Planning) => void;
  isGuest?: boolean;
  printRef?: React.RefObject<HTMLDivElement | null>;
}

export const PlanningTable = ({ planning, partners, onUpdate, isGuest = false, printRef }: Props) => {
  // The template is only used as a fallback for older planning docs that
  // don't yet have snapshotted labels. New plannings carry their own labels.
  const planningTemplates = useProjectStore((state) => state.planningTemplates);
  const template = useMemo(
    () => planningTemplates.find((t) => t.id === planning.planning_template_id) || null,
    [planningTemplates, planning.planning_template_id],
  );

  const startDate = useMemo(() => {
    // Parse YYYY-MM-DD as local time to avoid UTC timezone shift
    const [y, m, d] = planning.start_date.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [planning.start_date]);

  const cols = useMemo(
    () => computeWeekColumns(startDate, planning.model),
    [startDate, planning.model],
  );

  const monthGroups = useMemo(() => buildMonthGroups(cols), [cols]);

  // Artisan filter: which artisan IDs are visible
  const allArtisanIds = useMemo(() => {
    const ids = new Set<string>();
    planning.sections.forEach((s) =>
      s.artisan_ids.forEach((id) => ids.add(id)),
    );
    return Array.from(ids);
  }, [planning.sections]);

  const [hiddenArtisans, setHiddenArtisans] = useState<Set<string>>(new Set());

  const toggleHideArtisan = (id: string) => {
    setHiddenArtisans((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Scroll navigation
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const hasDragged = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const [dragging, setDragging] = useState(false);

  // Drag hint overlay — shown once per browser
  const HINT_KEY = "planning_drag_hint_seen";
  const [hintVisible, setHintVisible] = useState(true);
  const [hintFading, setHintFading] = useState(false);

  const dismissHint = useCallback(() => {
    if (!hintVisible) return;
    setHintFading(true);
    localStorage.setItem(HINT_KEY, "1");
    setTimeout(() => setHintVisible(false), 400);
  }, [hintVisible]);

  useEffect(() => {
    if (!hintVisible) return;
    const t = setTimeout(dismissHint, 4500);
    return () => clearTimeout(t);
  }, [hintVisible, dismissHint]);

  useEffect(() => {
    const onWindowMouseUp = () => {
      isDragging.current = false;
      setDragging(false);
    };
    window.addEventListener("mouseup", onWindowMouseUp);
    return () => window.removeEventListener("mouseup", onWindowMouseUp);
  }, []);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    dismissHint();
    isDragging.current = true;
    hasDragged.current = false;
    dragStartX.current = e.clientX;
    dragScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    if (Math.abs(dx) > 5) {
      hasDragged.current = true;
      setDragging(true);
      if (scrollRef.current)
        scrollRef.current.scrollLeft = dragScrollLeft.current - dx;
    }
  };

  const stopDragging = () => {
    isDragging.current = false;
    setDragging(false);
  };

  // Debounced save
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [localPlanning, setLocalPlanning] = useState<Planning>(planning);

  // Sync if parent sends a new planning
  useEffect(() => {
    setLocalPlanning(planning);
  }, [planning.id, planning.updated_at]);

  const scheduleSave = useCallback(
    (updated: Planning) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        onUpdate(updated);
      }, 800);
    },
    [onUpdate],
  );

  function toggleWeek(
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) {
    if (isGuest) return;
    setLocalPlanning((prev) => {
      const updated: Planning = {
        ...prev,
        sections: prev.sections.map((section) => {
          if (section.section_key !== sectionKey) return section;
          return {
            ...section,
            steps: section.steps.map((step) => {
              if (step.step_key !== stepKey) return step;
              const currentWeeks = step.colored_weeks[artisanId] || [];
              const hasWeek = currentWeeks.includes(weekIndex);
              return {
                ...step,
                colored_weeks: {
                  ...step.colored_weeks,
                  [artisanId]: hasWeek
                    ? currentWeeks.filter((w) => w !== weekIndex)
                    : [...currentWeeks, weekIndex],
                },
              };
            }),
          };
        }),
      };
      scheduleSave(updated);
      return updated;
    });
  }

  const handleToggleWeek = (
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) => {
    if (hasDragged.current) return;
    toggleWeek(sectionKey, stepKey, artisanId, weekIndex);
  };

  const partnerMap = useMemo(() => {
    const m: Record<string, PartnerInfos> = {};
    partners.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [partners]);

  // ─── Cell width ───────────────────────────────────────────────────────────
  // Fixed column widths. The grid will overflow horizontally on narrow
  // displays; the user can dezoom the browser if needed. Vertical scroll
  // is removed (no max-height) so the whole grid is visible at once.
  const COL_W = 28; // px per week column
  const LABEL_W = 260; // px for the left label column

  return (
    <div className="flex flex-col gap-4">
      {isGuest && (
        <div className="text-h3 text-red-500 font-medium">
          Ce planning est un planning prévisionnel d'interventions et est susceptible d'évoluer.
        </div>
      )}
      {/* Artisan filter */}
      {!isGuest && allArtisanIds.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          {allArtisanIds.map((id) => {
            const p = partnerMap[id];
            const name = p?.company_name || p?.display_name || id;
            const hidden = hiddenArtisans.has(id);
            return (
              <button
                key={id}
                onClick={() => toggleHideArtisan(id)}
                className={`px-3 py-1 rounded-full text-h4 border transition-all ${
                  hidden
                    ? "bg-white text-neutral-400 border-neutral-200"
                    : "bg-neutral-900 text-white border-neutral-900"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      {/* Non-scrollable table — overflows its container naturally so
          users can zoom out the browser to see more, or page-scroll
          horizontally to reach later weeks. */}
      <div className="relative">

        <div
          ref={(el) => {
            (scrollRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
            if (printRef) (printRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }}
          className="planning-print-container rounded-xl border border-neutral-200"
        >
          <table
            className="border-separate border-spacing-0"
            style={{ minWidth: LABEL_W + cols.length * COL_W }}
          >
            <thead className="sticky top-0 z-30">
              {/* Row 1 — months (merged) */}
              <tr>
                <td
                  className="sticky left-0 z-40 bg-neutral-100 border border-neutral-200 px-2 py-1 text-h4 text-neutral-500"
                  style={{ width: LABEL_W, minWidth: LABEL_W }}
                >
                  Mois
                </td>
                {monthGroups.map((g, i) => (
                  <td
                    key={i}
                    className="relative z-30 bg-neutral-100 border border-neutral-200 text-h4 font-medium text-center"
                    colSpan={g.colSpan}
                    style={{ minWidth: g.colSpan * COL_W }}
                  >
                    {g.label}
                  </td>
                ))}
              </tr>

              {/* Row 3 — ISO week numbers */}
              <tr>
                <td
                  className="sticky left-0 z-40 bg-neutral-50 border border-neutral-200 px-2 py-1 text-h4 text-neutral-400"
                  style={{ width: LABEL_W, minWidth: LABEL_W }}
                >
                  Semaine
                </td>
                {cols.map((col) => (
                  <td
                    key={col.index}
                    className={`relative z-30 border border-neutral-200 text-center text-h4 font-medium ${
                      col.isBlocked
                        ? "bg-gray-300 text-gray-400"
                        : "bg-neutral-50 text-neutral-600"
                    }`}
                    style={{ width: COL_W, minWidth: COL_W }}
                  >
                    {col.isoWeek}
                  </td>
                ))}
              </tr>
            </thead>

            <tbody>
              {localPlanning.sections.map((sectionData) => {
                const sectionDef = resolveSectionFromPlanning(sectionData, template);
                const visibleArtisans = sectionData.artisan_ids.filter(
                  (id) => !hiddenArtisans.has(id),
                );

                return (
                  <SectionRows
                    key={sectionData.section_key}
                    sectionDef={sectionDef}
                    sectionData={sectionData}
                    visibleArtisanIds={visibleArtisans}
                    partnerMap={partnerMap}
                    cols={cols}
                    colW={COL_W}
                    labelW={LABEL_W}
                    onToggleWeek={handleToggleWeek}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── SectionRows sub-component ────────────────────────────────────────────────

interface SectionRowsProps {
  sectionDef: ResolvedPlanningSection;
  sectionData: PlanningSection;
  visibleArtisanIds: string[];
  partnerMap: Record<string, PartnerInfos>;
  cols: WeekColumn[];
  colW: number;
  labelW: number;
  onToggleWeek: (
    sectionKey: string,
    stepKey: string,
    artisanId: string,
    weekIndex: number,
  ) => void;
}

function SectionRows({
  sectionDef,
  sectionData,
  visibleArtisanIds,
  partnerMap,
  cols,
  colW,
  labelW,
  onToggleWeek,
}: SectionRowsProps) {
  const artisanNames = visibleArtisanIds.map(
    (id) => partnerMap[id]?.company_name || partnerMap[id]?.display_name || id,
  );

  const hbg = sectionDef.header_bg;
  const htxt = sectionDef.header_text;
  const cellBg = sectionDef.cell_color;

  return (
    <>
      {/* Section header row */}
      <tr>
        <td
          className="sticky left-0 z-10 px-2 py-1 text-h3 font-bold"
          style={{ width: labelW, minWidth: labelW, backgroundColor: hbg, color: htxt }}
        >
          {sectionDef.label}
          {artisanNames.length > 0 && (
            <span className="ml-2 font-normal text-h4 opacity-80">
              {artisanNames.join(", ")}
            </span>
          )}
        </td>
        {cols.map((col) => (
          <td
            key={col.index}
            className="border-b border-neutral-200"
            style={{
              width: colW,
              minWidth: colW,
              backgroundColor: col.isBlocked ? "#D1D5DB" : hbg,
              opacity: col.isBlocked ? 1 : 0.3,
            }}
          >{"\u00A0"}</td>
        ))}
      </tr>

      {/* Step rows (one per artisan per step) */}
      {sectionDef.steps.map((stepDef) => {
        const stepData = sectionData.steps.find(
          (s) => s.step_key === stepDef.step_key,
        );

        if (visibleArtisanIds.length === 0) {
          // No artisans assigned — show template weeks if available
          const templateWeeks = stepData?.colored_weeks["_template"] || [];
          return (
            <tr key={stepDef.step_key}>
              <td
                className="sticky left-0 z-10 px-3 py-1 text-h4 text-neutral-500 bg-white border-b border-neutral-100"
                style={{ width: labelW, minWidth: labelW }}
              >
                {stepDef.label}
              </td>
              {cols.map((col) => {
                const isColored = templateWeeks.includes(col.index);
                return (
                  <td
                    key={col.index}
                    onClick={() =>
                      !col.isBlocked &&
                      onToggleWeek(
                        sectionData.section_key,
                        stepDef.step_key,
                        "_template",
                        col.index,
                      )
                    }
                    className={`border-b border-neutral-100 transition-colors ${
                      col.isBlocked
                        ? "cursor-not-allowed"
                        : isColored
                          ? "cursor-pointer"
                          : "bg-white hover:bg-neutral-50 cursor-pointer"
                    }`}
                    style={{
                      width: colW,
                      minWidth: colW,
                      height: 24,
                      backgroundColor: col.isBlocked
                        ? "#E5E7EB"
                        : isColored
                          ? cellBg
                          : undefined,
                    }}
                    title={
                      col.isBlocked
                        ? "Congés"
                        : `S${col.isoWeek} — ${col.monthLabel}`
                    }
                  >{"\u00A0"}</td>
                );
              })}
            </tr>
          );
        }

        return visibleArtisanIds.map((artisanId, aIdx) => {
          const artisanName =
            partnerMap[artisanId]?.company_name ||
            partnerMap[artisanId]?.display_name ||
            artisanId;
          const coloredWeeks = stepData?.colored_weeks[artisanId] || [];

          return (
            <tr key={`${stepDef.step_key}-${artisanId}`}>
              <td
                className="sticky left-0 z-10 px-3 py-1 bg-white border-b border-neutral-100"
                style={{ width: labelW, minWidth: labelW }}
              >
                <div className="flex flex-col">
                  {aIdx === 0 && (
                    <span className="text-h4 text-neutral-700">
                      {stepDef.label}
                    </span>
                  )}
                  {visibleArtisanIds.length > 1 && (
                    <span className="text-h4 text-neutral-400 italic">
                      {artisanName}
                    </span>
                  )}
                </div>
              </td>
              {cols.map((col) => {
                const isColored = coloredWeeks.includes(col.index);
                return (
                  <td
                    key={col.index}
                    onClick={() =>
                      !col.isBlocked &&
                      onToggleWeek(
                        sectionData.section_key,
                        stepDef.step_key,
                        artisanId,
                        col.index,
                      )
                    }
                    className={`border-b border-neutral-100 transition-colors ${
                      col.isBlocked
                        ? "cursor-not-allowed"
                        : isColored
                          ? "cursor-pointer"
                          : "bg-white hover:bg-neutral-50 cursor-pointer"
                    }`}
                    style={{
                      width: colW,
                      minWidth: colW,
                      height: 24,
                      backgroundColor: col.isBlocked
                        ? "#E5E7EB"
                        : isColored
                          ? cellBg
                          : undefined,
                    }}
                    title={
                      col.isBlocked
                        ? "Congés"
                        : `S${col.isoWeek} — ${col.monthLabel}`
                    }
                  >{"\u00A0"}</td>
                );
              })}
            </tr>
          );
        });
      })}
    </>
  );
}
